import type {PlaceOfService} from 'api/v2';
import type {
  Checklist,
  ContactMethodState,
  EncounterState,
  NarrativeAction,
  NarrativeState,
} from '../NoteForm';
import {today} from '../NoteForm';
import type {
  ChangeStatus,
  ContactMethod,
  NotReachedReason,
  ServiceCode,
} from './choices';
import {NO_ACTION, NO_DIRECT_CONTACT} from './choices';

export type {Checklist};

// The date of service whose catalog allowed the pick, so a date change needs a fresh check
export type PlaceOfServicePick = PlaceOfService & {confirmedFor: string};

export type ContactNoteState = EncounterState &
  ContactMethodState &
  NarrativeState & {
    contactMethod: ContactMethod | null;
    notReachedReason: NotReachedReason | null;
    notReachedReasonOther: string;
    nextAttemptAt: string;
    notReachedNote: string;
    serviceCode: ServiceCode | null;
    placeOfService: PlaceOfServicePick | null;
    placeOfServiceOther: string;
    activities: Checklist;
    specificNeed: string;
    startTime: string;
    endTime: string;
    serviceDescription: string;
    reasons: Checklist;
    objectiveReason: string;
    changeStatus: ChangeStatus | null;
    needs: Checklist;
    relevantChange: string;
    observation: string;
    responses: Checklist;
    responseOutcome: string;
    followUps: Checklist;
    followUpPlan: string;
    followUpAt: string;
  };

export type ChecklistField =
  'activities' | 'reasons' | 'needs' | 'responses' | 'followUps';

type SettableField = Exclude<
  keyof ContactNoteState,
  ChecklistField | 'narrative'
>;

export type ContactNoteAction =
  | {
      [K in SettableField]: {
        type: 'set';
        field: K;
        value: ContactNoteState[K];
      };
    }[SettableField]
  | {type: 'toggle'; field: ChecklistField; key: string}
  | {type: 'setOther'; field: ChecklistField; value: string}
  | NarrativeAction;

const emptyChecklist = (): Checklist => ({selected: [], other: ''});

export const createInitialState = (): ContactNoteState => ({
  member: null,
  dateOfService: today(),
  contactMethod: null,
  contactMethodDetail: '',
  notReachedReason: null,
  notReachedReasonOther: '',
  nextAttemptAt: '',
  notReachedNote: '',
  serviceCode: null,
  placeOfService: null,
  placeOfServiceOther: '',
  activities: emptyChecklist(),
  specificNeed: '',
  startTime: '',
  endTime: '',
  serviceDescription: '',
  reasons: emptyChecklist(),
  objectiveReason: '',
  changeStatus: null,
  needs: emptyChecklist(),
  relevantChange: '',
  observation: '',
  responses: emptyChecklist(),
  responseOutcome: '',
  followUps: emptyChecklist(),
  followUpPlan: '',
  followUpAt: '',
  narrative: null,
});

const NONE_OF_THE_ABOVE_KEY: Partial<Record<ChecklistField, string>> = {
  followUps: NO_ACTION,
  responses: NO_DIRECT_CONTACT,
};

const toggle = (
  selected: string[],
  key: string,
  exclusiveKey: string | undefined,
) => {
  if (selected.includes(key)) {
    return selected.filter((item) => item !== key);
  }
  if (key === exclusiveKey) {
    return [key];
  }
  return [...selected.filter((item) => item !== exclusiveKey), key];
};

const isShallowEqual = (a: ContactNoteState, b: ContactNoteState) =>
  (Object.keys(a) as (keyof ContactNoteState)[]).every(
    (key) => a[key] === b[key],
  );

const applyAction = (
  state: ContactNoteState,
  action: ContactNoteAction,
): ContactNoteState => {
  switch (action.type) {
    case 'set':
      return {...state, [action.field]: action.value};
    case 'toggle':
      return {
        ...state,
        [action.field]: {
          ...state[action.field],
          selected: toggle(
            state[action.field].selected,
            action.key,
            NONE_OF_THE_ABOVE_KEY[action.field],
          ),
        },
      };
    case 'setOther':
      return {
        ...state,
        [action.field]: {...state[action.field], other: action.value},
      };
    case 'editNarrative':
      return {...state, narrative: action.value};
    case 'resetNarrative':
      return {...state, narrative: null};
    default:
      // Untyped dispatches from NoteForm parts still reach here at runtime
      action satisfies never;
      return state;
  }
};

export const contactNoteReducer = (
  state: ContactNoteState,
  action: ContactNoteAction,
): ContactNoteState => {
  const next = applyAction(state, action);
  // Re-sending the current value keeps the same state, so the form doesn't re-render
  return isShallowEqual(next, state) ? state : next;
};
